import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useState } from 'react'
import StructuredData from '../components/StructuredData'
import './CaseworkLandingPage.css'

interface QuoteFormData {
  name: string
  email: string
  phone: string
  company?: string
  referral?: string
  projectType: string
}

function CaseworkLandingPage() {
  const formRef = useRef<HTMLDivElement>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitError, setIsSubmitError] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<QuoteFormData>()

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mojnjjob'

  const onSubmit = async (data: QuoteFormData) => {
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company || '',
          referral: data.referral || '',
          projectType: data.projectType,
          _subject: 'Lab Casework Quote Request',
        }),
      })
      if (!res.ok) throw new Error('Submit failed')
      setIsSubmitted(true)
      reset()
      setTimeout(() => setIsSubmitted(false), 8000)
    } catch {
      setIsSubmitError(true)
      setTimeout(() => setIsSubmitError(false), 5000)
    }
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Laboratory Casework',
    description:
      'Metal, P-lam, and phenolic laboratory casework. Durable, chemical-resistant, and custom-configured for your lab space.',
    brand: { '@type': 'Brand', name: 'Laboratory Specialists' },
    offers: { '@type': 'Offer', priceCurrency: 'USD', availability: 'https://schema.org/UponRequest' },
  }

  return (
    <div className="casework-landing-page">
      <StructuredData data={structuredData} />

      {/* Hero: headline + sub-headline + proof + CTA */}
      <section className="casework-hero">
        <div className="casework-hero-bg" aria-hidden />
        <div className="container casework-hero-inner">
          <h1 className="casework-hero-title">
            Lab Casework Built to Last—And Fit Your Space Perfectly
          </h1>
          <p className="casework-hero-subtitle">
            Metal, P-lam, and phenolic casework designed for durability and chemical resistance. We configure, fabricate, and install to match your lab's exact needs.
          </p>
          <div className="casework-hero-cta">
            <button type="button" className="casework-cta-button" onClick={scrollToForm}>
              Get Your Free Quote
            </button>
            <p className="casework-hero-cta-note">Custom quote in 24 hours · No obligation</p>
          </div>
        </div>
      </section>

      {/* Three objections (below the fold) */}
      <section className="casework-objections">
        <div className="container">
          <ul className="casework-bullets">
            <li>
              <strong>Not sure which material is right?</strong> We'll recommend metal, P-lam, or phenolic casework based on your chemicals, budget, and durability requirements.
            </li>
            <li>
              <strong>Worried about custom sizing and configurations?</strong> We design casework to maximize your lab space, accommodate utilities, and support your workflow.
            </li>
            <li>
              <strong>Need one partner for the whole job?</strong> We handle design, fabrication, countertops, and installation—one quote, one timeline.
            </li>
          </ul>
        </div>
      </section>

      {/* Quote form - primary conversion */}
      <section className="casework-form-section" ref={formRef} id="quote-form">
        <div className="container">
          <div className="casework-form-card">
            <h2 className="casework-form-title">Get Your Custom Quote</h2>
            <p className="casework-form-lead-magnet">
              Get a custom quote for metal, P-lam, or phenolic casework—no obligation.
            </p>

            {isSubmitted ? (
              <div className="casework-form-success">
                <h3>Thanks—we'll be in touch within 24 hours.</h3>
                <p>We'll email or call with your quote and next steps.</p>
              </div>
            ) : (
              <form className="casework-form" onSubmit={handleSubmit(onSubmit)}>
                {isSubmitError && (
                  <p className="casework-form-error" role="alert">
                    Something went wrong. Please try again or call us at (541) 658-9374.
                  </p>
                )}
                <div className="casework-form-group">
                  <label htmlFor="casework-name">
                    Full Name <span className="casework-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="casework-name"
                    {...register('name', {
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Please enter your full name' },
                    })}
                    aria-invalid={errors.name ? 'true' : 'false'}
                    placeholder="First and Last Name"
                  />
                  {errors.name && (
                    <span className="casework-error" role="alert">
                      {errors.name.message}
                    </span>
                  )}
                </div>

                <div className="casework-form-group">
                  <label htmlFor="casework-email">
                    Email <span className="casework-required">*</span>
                  </label>
                  <input
                    type="email"
                    id="casework-email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email',
                      },
                    })}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    placeholder="Email Address"
                  />
                  {errors.email && (
                    <span className="casework-error" role="alert">
                      {errors.email.message}
                    </span>
                  )}
                </div>

                <div className="casework-form-group">
                  <label htmlFor="casework-phone">
                    Phone <span className="casework-required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="casework-phone"
                    {...register('phone', {
                      required: 'Phone is required',
                      pattern: {
                        value: /^[\d\s\-+()]+$/,
                        message: 'Please enter a valid phone number',
                      },
                    })}
                    aria-invalid={errors.phone ? 'true' : 'false'}
                    placeholder="123-456-7890"
                  />
                  {errors.phone && (
                    <span className="casework-error" role="alert">
                      {errors.phone.message}
                    </span>
                  )}
                </div>

                <div className="casework-form-group">
                  <label htmlFor="casework-company">Company / Institution (optional)</label>
                  <input
                    type="text"
                    id="casework-company"
                    {...register('company')}
                    placeholder="Your lab or company name"
                  />
                </div>

                <div className="casework-form-group">
                  <label htmlFor="casework-referral">How did you hear about us? (optional)</label>
                  <input
                    type="text"
                    id="casework-referral"
                    {...register('referral')}
                    placeholder="e.g. Google, referral, trade show..."
                  />
                </div>

                <div className="casework-form-group">
                  <label htmlFor="casework-projectType">
                    What do you need? <span className="casework-required">*</span>
                  </label>
                  <select
                    id="casework-projectType"
                    {...register('projectType', { required: 'Please select an option' })}
                    aria-invalid={errors.projectType ? 'true' : 'false'}
                  >
                    <option value="">Select...</option>
                    <option value="new-lab">New lab casework</option>
                    <option value="metal-casework">Metal casework</option>
                    <option value="p-lam-casework">P-lam casework</option>
                    <option value="phenolic-casework">Phenolic casework</option>
                    <option value="replacement">Replacement / retrofit</option>
                    <option value="materials-only">Materials quote only</option>
                    <option value="not-sure">Not sure yet—need guidance</option>
                  </select>
                  {errors.projectType && (
                    <span className="casework-error" role="alert">
                      {errors.projectType.message}
                    </span>
                  )}
                </div>

                <p className="casework-privacy">
                  By submitting you agree to our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                  . We won't share your information.
                </p>

                <button type="submit" className="casework-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Get My Quote'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="casework-legal">
        <div className="container">
          <p className="casework-legal-text">
            Laboratory Specialists. Lab equipment installation and casework systems. Available in various configurations and finishes.{' '}
            <a href="/privacy">Privacy Policy</a> · <a href="/contact">Contact</a>
          </p>
        </div>
      </section>
    </div>
  )
}

export default CaseworkLandingPage
